import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles, Loader2, FileText, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { sendChat } from "../lib/api";
import { useQuote } from "../context/QuoteContext";

const STORAGE_KEY = "nsp_chat_session";

const getSessionId = () => {
    let sid = localStorage.getItem(STORAGE_KEY);
    if (!sid) {
        sid = `nsp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem(STORAGE_KEY, sid);
    }
    return sid;
};

const TEASER_KEY = "nsp_chat_teaser_seen";

const SUGGESTIONS = [
    "Devo scavare una buca di 2 m, cosa mi consigli?",
    "Quanto costa noleggiare un escavatore per una settimana?",
    "Avete attrezzature per demolire un muro?",
    "Quale gruppo elettrogeno serve per un cantiere medio?",
];

/* Renderer per markdown nelle risposte dell'assistente.
   Mantiene lo stile compatto della chat e usa i colori brand NSP. */
const MARKDOWN_COMPONENTS = {
    p: ({ node, ...props }) => <p className="my-1.5 first:mt-0 last:mb-0" {...props} />,
    strong: ({ node, ...props }) => (
        <strong className="font-bold text-nsp-green" {...props} />
    ),
    em: ({ node, ...props }) => <em className="italic" {...props} />,
    ul: ({ node, ...props }) => (
        <ul className="list-disc pl-5 my-2 space-y-1" {...props} />
    ),
    ol: ({ node, ...props }) => (
        <ol className="list-decimal pl-5 my-2 space-y-1" {...props} />
    ),
    li: ({ node, ...props }) => <li className="leading-snug" {...props} />,
    h1: ({ node, ...props }) => (
        <h3 className="font-bold text-nsp-green uppercase tracking-wide text-sm mt-3 mb-1.5" {...props} />
    ),
    h2: ({ node, ...props }) => (
        <h3 className="font-bold text-nsp-green uppercase tracking-wide text-sm mt-3 mb-1.5" {...props} />
    ),
    h3: ({ node, ...props }) => (
        <h4 className="font-bold text-nsp-green text-sm mt-2 mb-1" {...props} />
    ),
    code: ({ node, inline, ...props }) =>
        inline ? (
            <code className="bg-nsp-bg text-nsp-green px-1 py-0.5 text-[12px] font-mono" {...props} />
        ) : (
            <code className="block bg-nsp-bg text-nsp-green p-2 my-2 text-[12px] font-mono overflow-x-auto" {...props} />
        ),
    a: ({ node, ...props }) => (
        <a
            className="text-nsp-green underline font-semibold hover:text-nsp-green-dark"
            target="_blank"
            rel="noreferrer"
            {...props}
        />
    ),
    hr: () => <hr className="my-2 border-nsp-border" />,
    table: ({ node, ...props }) => (
        <div className="overflow-x-auto my-2">
            <table className="w-full text-xs border-collapse" {...props} />
        </div>
    ),
    th: ({ node, ...props }) => (
        <th className="border border-nsp-border bg-nsp-bg px-2 py-1 text-left font-bold" {...props} />
    ),
    td: ({ node, ...props }) => (
        <td className="border border-nsp-border px-2 py-1" {...props} />
    ),
};

export const ChatWidget = () => {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([
        {
            role: "assistant",
            content:
                "Ciao! Sono l'assistente NSP. Posso aiutarti a scegliere l'attrezzatura giusta, darti info sui prezzi o consigliarti la durata di noleggio più conveniente. Cosa ti serve oggi?",
        },
    ]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [panelStyle, setPanelStyle] = useState({});
    const [teaser, setTeaser] = useState(false);
    const sessionId = useRef(getSessionId());
    const scrollRef = useRef(null);
    const { applyChatAction } = useQuote();

    const scrollToBottom = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, loading]);

    /* Su mobile la tastiera riduce il viewport visibile: adattiamo il pannello
       all'area realmente visibile (visualViewport) così l'input resta sopra la
       tastiera e i messaggi vengono riancorati in basso. */
    useEffect(() => {
        if (!open) return undefined;
        const vv = window.visualViewport;
        if (!vv) return undefined;
        const update = () => {
            if (window.innerWidth >= 768) {
                setPanelStyle({});
                return;
            }
            setPanelStyle({
                height: `${vv.height}px`,
                top: `${vv.offsetTop}px`,
                bottom: "auto",
            });
            requestAnimationFrame(scrollToBottom);
            setTimeout(scrollToBottom, 250);
        };
        update();
        vv.addEventListener("resize", update);
        vv.addEventListener("scroll", update);
        window.addEventListener("resize", update);
        return () => {
            vv.removeEventListener("resize", update);
            vv.removeEventListener("scroll", update);
            window.removeEventListener("resize", update);
        };
    }, [open]);

    const dismissTeaser = () => {
        setTeaser(false);
        try {
            sessionStorage.setItem(TEASER_KEY, "1");
        } catch (_) {}
    };

    /* Fumetto proattivo: dopo qualche secondo di navigazione invita a chattare (1 volta per sessione). */
    useEffect(() => {
        try {
            if (sessionStorage.getItem(TEASER_KEY)) return undefined;
        } catch (_) {}
        const t = setTimeout(() => setTeaser(true), 6000);
        return () => clearTimeout(t);
    }, []);

    useEffect(() => {
        if (open && teaser) dismissTeaser();
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handler = (e) => {
            setOpen(true);
            if (e.detail?.prefill) setInput(e.detail.prefill);
        };
        window.addEventListener("nsp:open-chat", handler);
        return () => window.removeEventListener("nsp:open-chat", handler);
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    const handleSend = async (text) => {
        const msg = (text ?? input).trim();
        if (!msg || loading) return;
        setInput("");
        setMessages((prev) => [...prev, { role: "user", content: msg }]);
        setLoading(true);
        try {
            const data = await sendChat(sessionId.current, msg);
            setMessages((prev) => [
                ...prev,
                { role: "assistant", content: data.reply },
            ]);
            if (data.actions?.length) {
                data.actions.forEach((a) => applyChatAction(a));
                setMessages((prev) => [
                    ...prev,
                    { role: "assistant", quoteUpdate: true, content: "" },
                ]);
            }
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                "Servizio temporaneamente non disponibile. Riprova tra poco o chiama 327 2237610.";
            setMessages((prev) => [
                ...prev,
                { role: "assistant", content: detail, error: true },
            ]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {/* Fumetto proattivo sopra il pulsante chat */}
            {!open && teaser && (
                <div
                    data-testid="chat-teaser"
                    className="fixed bottom-[8.75rem] right-4 md:bottom-[5.25rem] md:right-6 z-50 max-w-[300px] animate-in fade-in slide-in-from-bottom-3 duration-500"
                >
                    <div className="relative bg-white border-[3px] border-nsp-yellow rounded-2xl rounded-br-md shadow-[0_12px_30px_-8px_rgba(0,0,0,0.45)] p-4 pr-9">
                        <button
                            type="button"
                            data-testid="chat-teaser-close"
                            onClick={dismissTeaser}
                            aria-label="Chiudi suggerimento"
                            className="absolute top-2 right-2 p-1 rounded-full text-nsp-green/50 hover:text-nsp-green hover:bg-nsp-yellow-dark/20 transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                        <div className="flex items-start gap-3">
                            <span className="w-9 h-9 rounded-xl bg-nsp-green flex items-center justify-center flex-shrink-0">
                                <Sparkles className="w-4.5 h-4.5 text-nsp-yellow" strokeWidth={2.5} />
                            </span>
                            <div className="text-[15px] leading-snug text-nsp-green font-medium">
                                <b>Ciao!</b> Posso aiutarti a scegliere il mezzo giusto o a
                                preparare un preventivo?
                            </div>
                        </div>
                        <button
                            type="button"
                            data-testid="chat-teaser-open"
                            onClick={() => {
                                dismissTeaser();
                                setOpen(true);
                            }}
                            className="mt-3 w-full rounded-full bg-nsp-green text-nsp-yellow px-4 py-2.5 text-[13px] font-bold uppercase tracking-widest hover:bg-nsp-green-dark transition-colors active:scale-95"
                        >
                            Chiedi all'assistente
                        </button>
                        <span className="absolute -bottom-2 right-6 w-3.5 h-3.5 bg-nsp-yellow border-b-[3px] border-r-[3px] border-white rotate-45" />
                    </div>
                </div>
            )}

            {/* Floating Trigger — pill giallo con glow */}
            {!open && (
                <button
                    data-testid="chat-trigger"
                    onClick={() => setOpen(true)}
                    className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-50 bg-nsp-yellow text-nsp-green rounded-full pl-4 pr-5 py-3.5 font-bold text-sm shadow-xl shadow-nsp-yellow/40 hover:shadow-2xl hover:shadow-nsp-yellow/60 hover:scale-105 active:scale-95 transition-all flex items-center gap-2.5 border border-nsp-yellow-dark/30"
                >
                    <MessageCircle className="w-5 h-5" strokeWidth={2.5} />
                    <span className="hidden sm:inline">Chatta con noi</span>
                </button>
            )}

            {/* Chat Panel */}
            {open && (
                <div
                    data-testid="chat-panel"
                    style={panelStyle}
                    className="fixed inset-0 w-full h-[100dvh] rounded-none md:inset-auto md:bottom-6 md:right-6 md:w-[400px] md:h-[600px] md:max-h-[80vh] md:rounded-3xl z-[60] bg-white shadow-2xl border border-gray-100 flex flex-col overflow-hidden"
                >
                    {/* Header */}
                    <div className="bg-nsp-green text-white p-4 flex items-center justify-between flex-shrink-0">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-nsp-yellow rounded-2xl flex items-center justify-center">
                                <Sparkles className="w-5 h-5 text-nsp-green" strokeWidth={2.5} />
                            </div>
                            <div>
                                <div className="font-bold uppercase tracking-wider text-sm">
                                    Assistente NSP
                                </div>
                                <div className="text-nsp-yellow text-[10px] uppercase tracking-[0.18em] flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 bg-nsp-yellow rounded-full animate-pulse" />
                                    Online — IA Italiana
                                </div>
                            </div>
                        </div>
                        <button
                            data-testid="chat-close"
                            onClick={() => setOpen(false)}
                            className="p-2 hover:bg-nsp-green-dark"
                            aria-label="Chiudi chat"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="bg-nsp-green-dark/95 text-white/60 text-[10px] px-4 py-1.5 flex-shrink-0 leading-snug">
                        Risposte generate automaticamente (IA). Non inserire dati sensibili —{" "}
                        <a href="/privacy" target="_blank" rel="noreferrer" className="underline text-white/80">
                            informativa privacy
                        </a>
                    </div>

                    {/* Messages */}
                    <div
                        ref={scrollRef}
                        data-testid="chat-messages"
                        className="flex-1 min-h-0 overflow-y-auto overscroll-contain touch-pan-y p-4 pb-6 space-y-3 bg-nsp-bg"
                    >
                        {messages.map((m, idx) =>
                            m.quoteUpdate ? (
                                <div key={idx} className="flex justify-start" data-testid="chat-quote-update">
                                    <Link
                                        to="/preventivo"
                                        onClick={() => setOpen(false)}
                                        data-testid="chat-quote-link"
                                        className="group inline-flex items-center gap-2.5 bg-nsp-green text-white text-xs font-bold px-4 py-3 rounded-2xl rounded-tl-md shadow-md hover:bg-nsp-green-dark transition-colors"
                                    >
                                        <span className="w-7 h-7 rounded-full bg-nsp-yellow flex items-center justify-center flex-shrink-0">
                                            <FileText className="w-3.5 h-3.5 text-nsp-green" strokeWidth={2.5} />
                                        </span>
                                        Preventivo aggiornato — Aprilo
                                        <ArrowUpRight className="w-3.5 h-3.5 group-hover:rotate-45 transition-transform duration-300" />
                                    </Link>
                                </div>
                            ) : (
                            <div
                                key={idx}
                                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                            >
                                <div
                                    className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed rounded-2xl ${
                                        m.role === "user"
                                            ? "bg-nsp-green text-white rounded-tr-md"
                                            : m.error
                                              ? "bg-red-50 border border-red-200 text-red-900 rounded-tl-md"
                                              : "bg-white border border-nsp-border text-zinc-800 rounded-tl-md"
                                    }`}
                                >
                                    {m.role === "assistant" && !m.error ? (
                                        <div className="prose-chat">
                                            <ReactMarkdown
                                                remarkPlugins={[remarkGfm]}
                                                components={MARKDOWN_COMPONENTS}
                                            >
                                                {m.content}
                                            </ReactMarkdown>
                                        </div>
                                    ) : (
                                        <div className="whitespace-pre-wrap">{m.content}</div>
                                    )}
                                </div>
                            </div>
                            )
                        )}
                        {loading && (
                            <div className="flex justify-start" data-testid="chat-typing">
                                <div className="bg-white border border-nsp-border px-4 py-3 rounded-2xl rounded-tl-md">
                                    <span className="typing-dot"></span>
                                    <span className="typing-dot"></span>
                                    <span className="typing-dot"></span>
                                </div>
                            </div>
                        )}

                        {/* Suggestions */}
                        {messages.length === 1 && !loading && (
                            <div className="pt-4 space-y-2">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                                    Suggerimenti:
                                </div>
                                {SUGGESTIONS.map((s, i) => (
                                    <button
                                        key={i}
                                        data-testid={`chat-suggestion-${i}`}
                                        onClick={() => handleSend(s)}
                                        className="block w-full text-left text-xs bg-white border border-nsp-border rounded-2xl px-3 py-2 hover:border-nsp-green hover:bg-nsp-green hover:text-white transition-colors"
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Input */}
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleSend();
                        }}
                        className="border-t-2 border-nsp-green bg-white p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex gap-2 flex-shrink-0"
                    >
                        <input
                            data-testid="chat-input"
                            type="text"
                            placeholder="Scrivi un messaggio..."
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onFocus={() => {
                                requestAnimationFrame(scrollToBottom);
                                setTimeout(scrollToBottom, 300);
                            }}
                            disabled={loading}
                            maxLength={2000}
                            autoComplete="off"
                            className="flex-1 h-12 bg-nsp-bg border border-nsp-border px-3 text-base sm:text-sm focus:outline-none focus:border-nsp-green"
                        />
                        <button
                            data-testid="chat-send"
                            type="submit"
                            disabled={loading || !input.trim()}
                            className="w-12 h-12 bg-nsp-green text-nsp-yellow flex items-center justify-center hover:bg-nsp-yellow hover:text-nsp-green transition-colors disabled:opacity-40 border-b-2 border-nsp-yellow"
                        >
                            {loading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                <Send className="w-5 h-5" />
                            )}
                        </button>
                    </form>
                </div>
            )}
        </>
    );
};

export default ChatWidget;
